// src/composables/useBannerCropper.js
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { dataURLtoFile, toBase64, getLinkWithoutQueryParams } from '@/common-utils'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import Compress from 'compress.js'
import { IMAGE_COMPRESSER } from '@/constants'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'

export function useBannerCropper(profileGeneralRef) {
  // --- Section B: Reactive State ---
  const croppedImg = ref(null)
  const isCropping = ref(false)
  const uploadedFile = ref('')

  // --- Section C: Computed ---
  const hasBanner = computed(() => (profileGeneralRef.value?.banner?.length || 0) < 3)

  // --- Section D: Methods ---
  function clearImg() {
    croppedImg.value = ''
  }

  function getAvailableBannerIndex() {
    const bannersCount = ['1', '2', '3']
    const banners = profileGeneralRef.value?.banner || []
    const bannersIndex = banners.map(banner => banner[banner.indexOf('banner') + 6])
    const missedBanner = bannersCount.find(e => !bannersIndex.includes(e))
    return missedBanner || null
  }

  async function onSelectImage(e) {
    const file = e.target.files[0]
    uploadedFile.value = file
    if (!/\.(gif|jpg|jpeg|png|bmp|GIF|JPG|PNG)$/.test(e.target.value)) {
      return false
    }
    croppedImg.value = await toBase64(file)
    e.target.value = ''
    return true
  }

  async function crop(cropperRef) {
    isCropping.value = true
    try {
      const { canvas } = cropperRef.getResult()
      const logoFile = canvas.toDataURL()
      let file = dataURLtoFile(logoFile, 'banner.png')
      if (file.size / 1024 > 1024) {
        const compress = new Compress()
        const data = await compress.compress([file], IMAGE_COMPRESSER)
        file = dataURLtoFile(data[0].prefix + data[0].data, 'banner.png')
      }
      if (file instanceof File) {
        const res = await store.dispatch(UPLOAD_DOCUMENTS, {
          fileType: `banner${getAvailableBannerIndex()}`,
          file,
        })
        profileGeneralRef.value.banner.push(res)
        clearImg()
        apiToastSuccess('Your file(s) are uploaded')
      }
    } catch (err) {
      apiToastWarning(err)
    } finally {
      isCropping.value = false
    }
  }

  async function deleteBanner(index, kpRequest, kpEndpoint) {
    profileGeneralRef.value.banner.splice(index, 1)
    const payload = { ...profileGeneralRef.value }
    payload.banner = profileGeneralRef.value.banner.map(img => getLinkWithoutQueryParams(img))
    try {
      await kpRequest({ ...kpEndpoint.profile.general, payload })
      apiToastSuccess(`banner ${index + 1} removed`)
      return { success: true }
    } catch {
      return { success: false }
    }
  }

  return {
    croppedImg,
    isCropping,
    uploadedFile,
    hasBanner,
    clearImg,
    getAvailableBannerIndex,
    onSelectImage,
    crop,
    deleteBanner,
  }
}
